import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useMatch, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PostList from './components/PostList';
import PostDetail from './components/PostDetail';
import TitleIndex from './components/TitleIndex';
import AboutPage from './components/AboutPage';
import WriterPublish from './components/WriterPublish';
import WriterControls from './components/WriterControls';
import WriterSeries, {
    AuthorPostSeriesLine,
    indexSeriesByPost,
    useAuthorSeries,
} from './components/WriterSeries';
import {
    saveCommentToken,
    removeCommentToken,
    purgeExpiredCommentTokens
} from './utils/commentTokens';
import { formatDocumentNumber } from './utils/documentNumber';
import { isVideoPost } from './utils/video';
import './App.css';
import { normalizeTagValue } from './utils/tagUtils';

// This legacy iteration of the site is mounted under /old (see src/index.js).
const OLD_BASE = '/old';

const HOME_TABS = Object.freeze([
    { id: 'index', label: 'Index' },
    { id: 'documents', label: 'Documents' },
    { id: 'titles', label: 'Titles' },
]);

const POSTS_PER_PAGE = 10;

function App() {
    const [posts, setPosts] = useState([]);
    const [postsPage, setPostsPage] = useState(1);
    const [postsTotalPages, setPostsTotalPages] = useState(0);
    const [allTags, setAllTags] = useState([]);
    const [tagGroups, setTagGroups] = useState([]);
    const [allPostTitles, setAllPostTitles] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [titlesLoading, setTitlesLoading] = useState(false);
    const [titlesError, setTitlesError] = useState(null);
    const [selectedPostId, setSelectedPostId] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);
    const [activePostId, setActivePostId] = useState(null);
    const [isAuthorPortalOpen, setIsAuthorPortalOpen] = useState(false);
    const [showAuthorAccess, setShowAuthorAccess] = useState(false);
    const [authorAccessPassphrase, setAuthorAccessPassphrase] = useState('');
    const [authorAccessError, setAuthorAccessError] = useState(null);
    const [authorPassphrase, setAuthorPassphrase] = useState('');
    const [authorPosts, setAuthorPosts] = useState([]);
    const [authorLoading, setAuthorLoading] = useState(false);
    const [authorPortalError, setAuthorPortalError] = useState(null);
    const [activeAuthorTab, setActiveAuthorTab] = useState('create');
    const [authorAccessStatus, setAuthorAccessStatus] = useState(null);
    const [isAuthorRegistration, setIsAuthorRegistration] = useState(false);
    const [authorInviteCode, setAuthorInviteCode] = useState('');
    const [authorRegistrationPassphrase, setAuthorRegistrationPassphrase] = useState('');
    const [authorRegistrationConfirm, setAuthorRegistrationConfirm] = useState('');
    const [authorRegistrationError, setAuthorRegistrationError] = useState(null);
    const [authorRegistrationLoading, setAuthorRegistrationLoading] = useState(false);
    const [isMasterAccess, setIsMasterAccess] = useState(false);
    const [masterAccessCode, setMasterAccessCode] = useState('');
    const [adminToken, setAdminToken] = useState('');
    const [adminTokenExpiresAt, setAdminTokenExpiresAt] = useState('');
    const [adminWriters, setAdminWriters] = useState([]);
    const [adminSelectedAuthorHash, setAdminSelectedAuthorHash] = useState('');
    const [adminWritersLoading, setAdminWritersLoading] = useState(false);
    const [activeHomeTab, setActiveHomeTab] = useState('documents');

    const selectedTagSet = useMemo(() => (
        selectedTags.length > 0 ? new Set(selectedTags) : new Set()
    ), [selectedTags]);

    const navigate = useNavigate();
    const location = useLocation();
    const postMatch = useMatch(`${OLD_BASE}/posts/:postId`);
    const routePostId = postMatch?.params?.postId || null;
    const isAboutPage = location.pathname.startsWith(`${OLD_BASE}/about`);
    const activePage = isAboutPage ? 'about' : 'home';
    const isAdminMode = typeof adminToken === 'string' && adminToken.trim().length > 0;
    const isOldHomePath = location.pathname === OLD_BASE || location.pathname === `${OLD_BASE}/`;
    const isHomeRoute = !isAuthorPortalOpen && isOldHomePath;
    const isReaderRoute = !isAuthorPortalOpen && Boolean(routePostId);

    // The writer's own series (admin: the selected author's), for the
    // My Series tab and the "Series: X · Part N" line on post cards.
    const authorSeries = useAuthorSeries({
        enabled: isAuthorPortalOpen,
        passphrase: authorPassphrase,
        adminToken: isAdminMode ? adminToken : '',
        adminAuthorHash: isAdminMode ? adminSelectedAuthorHash : '',
        // An expired admin token ends the admin session, like the portal's
        // other requests (clearAdminSession is declared further down).
        onUnauthorized: () => clearAdminSession(),
    });
    const authorSeriesByPost = useMemo(
        () => indexSeriesByPost(authorSeries.series),
        [authorSeries.series]
    );

    const resetAuthorRegistrationForm = () => {
        setAuthorInviteCode('');
        setAuthorRegistrationPassphrase('');
        setAuthorRegistrationConfirm('');
        setAuthorRegistrationError(null);
    };

    useEffect(() => {
        purgeExpiredCommentTokens();
    }, []);

    useEffect(() => {
        document.title = 'The Discerner :: Laymen on Agathology and Ponerology';
    }, []);

    // The revamped site's "Author Portal" button deep-links to /old?portal=1.
    const portalParamHandled = useRef(false);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('portal') === '1' && !portalParamHandled.current && !isAuthorPortalOpen) {
            // A valid stored admin session is restored by the effect below in
            // the same mount; wait for it so we open admin mode, not the prompt.
            const storedToken = sessionStorage.getItem('discerner_admin_token') || '';
            const expiryMs = Date.parse(sessionStorage.getItem('discerner_admin_expires_at') || '');
            const hasStoredSession = storedToken && Number.isFinite(expiryMs) && expiryMs > Date.now();
            if (hasStoredSession && !adminToken) {
                return;
            }
            portalParamHandled.current = true;
            const tab = params.get('tab');
            handleAuthorPortalButton(['create', 'documents', 'series'].includes(tab) ? tab : '');
        }
    }, [location.search, adminToken]);

    useEffect(() => {
        const storedToken = sessionStorage.getItem('discerner_admin_token') || '';
        const storedExpiresAt = sessionStorage.getItem('discerner_admin_expires_at') || '';
        const expiryMs = storedExpiresAt ? Date.parse(storedExpiresAt) : NaN;
        if (storedToken && Number.isFinite(expiryMs) && expiryMs > Date.now()) {
            setAdminToken(storedToken);
            setAdminTokenExpiresAt(storedExpiresAt);
        } else {
            sessionStorage.removeItem('discerner_admin_token');
            sessionStorage.removeItem('discerner_admin_expires_at');
        }
    }, []);

    useEffect(() => {
        fetchTags();
        fetchAllTitles();
    }, []);

    useEffect(() => {
        fetchPosts();
    }, [selectedTags, postsPage]);

    useEffect(() => {
        if (!isOldHomePath) {
            setActivePostId(null);
        }
    }, [isOldHomePath]);

    useEffect(() => {
        if (!isHomeRoute || typeof window === 'undefined') {
            return undefined;
        }

        const rafId = requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('scroll'));
        });

        return () => cancelAnimationFrame(rafId);
    }, [activeHomeTab, isHomeRoute]);

    useEffect(() => {
        setSelectedPostId(routePostId);
        if (!routePostId) {
            setSelectedPost(null);
            setDetailError(null);
            return;
        }

        if (selectedPost?._id !== routePostId) {
            setSelectedPost(null);
            setDetailError(null);
        }
    }, [routePostId, selectedPost]);

    const fetchTags = async () => {
        try {
            setError(null);
            const response = await axios.get(`${API_URL}/api/posts/tags`);
            const payload = response.data;
            const groups = Array.isArray(payload?.groups) ? payload.groups : [];
            const tags = Array.isArray(payload?.tags)
                ? payload.tags
                : (Array.isArray(payload) ? payload : []);

            setTagGroups(groups);
            setAllTags(tags);
        } catch (error) {
            console.error('[ERROR] Failed to fetch tags:', error);
            setError('Failed to load tags');
        }
    };

    const fetchPosts = async () => {
        setLoading(true);
        try {
            setError(null);
            const params = new URLSearchParams({
                page: String(postsPage),
                limit: String(POSTS_PER_PAGE),
                // Condoned Media video posts are listed on /videos, not
                // with the articles (older backends ignore the filter).
                type: 'article',
            });
            if (selectedTags.length > 0) {
                params.set('tags', selectedTags.join(','));
            }
            const response = await axios.get(`${API_URL}/api/posts?${params.toString()}`);
            const retrievedPosts = Array.isArray(response.data.posts)
                ? response.data.posts
                : (Array.isArray(response.data) ? response.data : []);
            const totalPages = Number(response.data.totalPages) || 0;
            setPosts(retrievedPosts);
            setPostsTotalPages(totalPages);

            if (totalPages > 0 && postsPage > totalPages) {
                setPostsPage(totalPages);
            }

            if (selectedPostId) {
                const updatedPostSummary = retrievedPosts.find(post => post._id === selectedPostId);
                if (updatedPostSummary) {
                    setSelectedPost(prev => (
                        prev && prev._id === selectedPostId
                            ? { ...prev, ...updatedPostSummary }
                            : prev
                    ));
                }
            }
        } catch (error) {
            console.error('[ERROR] Failed to fetch posts:', error);
            setError('Failed to access documents');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTitles = async () => {
        setTitlesLoading(true);
        try {
            setTitlesError(null);
            const response = await axios.get(`${API_URL}/api/posts/titles`);
            const retrievedTitles = response.data?.titles || response.data;
            setAllPostTitles(Array.isArray(retrievedTitles) ? retrievedTitles : []);
        } catch (error) {
            console.error('[ERROR] Failed to fetch title index:', error);
            setTitlesError('Failed to load title index');
        } finally {
            setTitlesLoading(false);
        }
    };

    const fetchAuthorPosts = async (passphrase, options = {}) => {
        const { suppressError = false } = options || {};

        setAuthorLoading(true);
        if (!suppressError) {
            setAuthorPortalError(null);
        }

        try {
            const response = await axios.post(
                `${API_URL}/api/posts/author/posts`,
                { passphrase }
            );
            const retrievedPosts = response.data.posts || [];
            setAuthorPosts(retrievedPosts);
            if (!suppressError) {
                setAuthorPortalError(null);
            }
            return { success: true, posts: retrievedPosts };
        } catch (error) {
            console.error('[ERROR] Failed to fetch author documents:', error);
            const message = error.response?.data?.message || 'Failed to retrieve author documents';
            if (!suppressError) {
                setAuthorPortalError(message);
            }
            return { success: false, message };
        } finally {
            setAuthorLoading(false);
        }
    };

    const getAdminHeaders = (token = adminToken) => {
        const trimmed = typeof token === 'string' ? token.trim() : '';
        return trimmed ? { Authorization: `Bearer ${trimmed}` } : {};
    };

    const clearAdminSession = () => {
        setAdminToken('');
        setAdminTokenExpiresAt('');
        setAdminWriters([]);
        setAdminSelectedAuthorHash('');
        setAdminWritersLoading(false);
        sessionStorage.removeItem('discerner_admin_token');
        sessionStorage.removeItem('discerner_admin_expires_at');
    };

    const fetchAdminWriters = async (token = adminToken) => {
        const headers = getAdminHeaders(token);
        if (!headers.Authorization) {
            return { success: false, message: 'Missing admin session' };
        }

        setAdminWritersLoading(true);
        setAuthorPortalError(null);

        try {
            const response = await axios.get(`${API_URL}/api/admin/writers`, { headers });
            const writers = Array.isArray(response.data?.writers) ? response.data.writers : [];
            setAdminWriters(writers);
            return { success: true, writers };
        } catch (error) {
            const status = error.response?.status;
            const message = error.response?.data?.message || 'Failed to load writers';
            if (status === 401) {
                clearAdminSession();
            }
            setAuthorPortalError(message);
            return { success: false, message };
        } finally {
            setAdminWritersLoading(false);
        }
    };

    const fetchAdminAuthorPosts = async (authorHash, token = adminToken, options = {}) => {
        const { suppressError = false } = options || {};
        const headers = getAdminHeaders(token);

        if (!headers.Authorization) {
            return { success: false, message: 'Missing admin session' };
        }

        const normalizedHash = typeof authorHash === 'string' ? authorHash.trim() : '';
        if (!normalizedHash) {
            return { success: false, message: 'Author required' };
        }

        setAuthorLoading(true);
        if (!suppressError) {
            setAuthorPortalError(null);
        }

        try {
            const response = await axios.get(
                `${API_URL}/api/admin/writers/${normalizedHash}/posts`,
                { headers }
            );
            const retrievedPosts = response.data?.posts || [];
            setAuthorPosts(retrievedPosts);
            if (!suppressError) {
                setAuthorPortalError(null);
            }
            return { success: true, posts: retrievedPosts };
        } catch (error) {
            const status = error.response?.status;
            const message = error.response?.data?.message || 'Failed to retrieve author documents';
            if (status === 401) {
                clearAdminSession();
            }
            if (!suppressError) {
                setAuthorPortalError(message);
            }
            return { success: false, message };
        } finally {
            setAuthorLoading(false);
        }
    };

    useEffect(() => {
        if (!selectedPostId) {
            return;
        }

        let isActive = true;

        const loadPostDetail = async () => {
            setDetailLoading(true);
            setDetailError(null);

            try {
                const response = await axios.get(`${API_URL}/api/posts/${selectedPostId}`);
                if (isActive) {
                    setSelectedPost(response.data);
                }
            } catch (error) {
                console.error('[ERROR] Failed to fetch document:', error);
                if (isActive) {
                    setDetailError('Failed to load document');
                    setSelectedPost(null);
                }
            } finally {
                if (isActive) {
                    setDetailLoading(false);
                }
            }
        };

        loadPostDetail();

        return () => {
            isActive = false;
        };
    }, [selectedPostId]);

    const toggleTag = (tag) => {
        const normalizedTag = normalizeTagValue(tag);
        if (!normalizedTag) {
            return;
        }
        setPostsPage(1);
        setSelectedTags((prev) =>
            prev.includes(normalizedTag)
                ? prev.filter((t) => t !== normalizedTag)
                : [...prev, normalizedTag]
        );
    };

    const selectTags = (nextTags) => {
        const normalized = (Array.isArray(nextTags) ? nextTags : [])
            .map((tag) => normalizeTagValue(tag))
            .filter(Boolean);
        setPostsPage(1);
        setSelectedTags([...new Set(normalized)]);
    };

    const clearFilters = () => {
        setPostsPage(1);
        setSelectedTags([]);
    };

    const handlePostsPageChange = (nextPage) => {
        const targetPage = Math.min(Math.max(1, nextPage), Math.max(1, postsTotalPages));
        if (targetPage === postsPage) {
            return;
        }
        setPostsPage(targetPage);
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    const handleSelectPost = (post) => {
        if (!post?._id) {
            return;
        }
        setSelectedPostId(post._id);
        setSelectedPost(null);
        setDetailError(null);
        navigate(`${OLD_BASE}/posts/${post._id}`);
    };

    const handleSelectPostById = (postId) => {
        if (!postId) {
            return;
        }
        setSelectedPostId(postId);
        setSelectedPost(null);
        setDetailError(null);
        navigate(`${OLD_BASE}/posts/${postId}`);
    };

    const handleReturnToList = () => {
        setSelectedPostId(null);
        setSelectedPost(null);
        setDetailError(null);
        navigate(OLD_BASE);
    };

    const handleNavigateHome = () => {
        if (isAuthorPortalOpen) {
            exitAuthorPortal();
            fetchPosts();
            return;
        }

        handleReturnToList();
    };

    const handleNavigateAbout = () => {
        if (isAuthorPortalOpen) {
            exitAuthorPortal();
        }

        setSelectedPostId(null);
        setSelectedPost(null);
        setDetailError(null);
        navigate(`${OLD_BASE}/about`);
    };

    const matchesComment = (comment, commentId) => {
        if (!comment || !commentId) {
            return false;
        }
        const currentId = comment._id || comment.id;
        return currentId === commentId;
    };

    const insertReply = (comments = [], parentId, newComment) => {
        if (!Array.isArray(comments) || comments.length === 0) {
            return comments;
        }

        return comments.map((comment) => {
            if (!comment) {
                return comment;
            }

            if (matchesComment(comment, parentId)) {
                const existingReplies = Array.isArray(comment.replies) ? comment.replies : [];
                return {
                    ...comment,
                    replies: [newComment, ...existingReplies],
                };
            }

            if (comment.replies && comment.replies.length > 0) {
                return {
                    ...comment,
                    replies: insertReply(comment.replies, parentId, newComment),
                };
            }

            return comment;
        });
    };

    const updateCommentContent = (comments = [], commentId, updatedComment) => {
        if (!Array.isArray(comments)) {
            return comments;
        }

        return comments.map((comment) => {
            if (!comment) {
                return comment;
            }

            if (matchesComment(comment, commentId)) {
                return {
                    ...comment,
                    ...updatedComment,
                };
            }

            if (comment.replies && comment.replies.length > 0) {
                return {
                    ...comment,
                    replies: updateCommentContent(comment.replies, commentId, updatedComment),
                };
            }

            return comment;
        });
    };

    const removeComment = (comments = [], commentId) => {
        if (!Array.isArray(comments)) {
            return comments;
        }

        return comments
            .filter(comment => comment && !matchesComment(comment, commentId))
            .map((comment) => {
                if (comment.replies && comment.replies.length > 0) {
                    return {
                        ...comment,
                        replies: removeComment(comment.replies, commentId),
                    };
                }
                return { ...comment };
            });
    };

    const findCommentInTree = (comments = [], commentId) => {
        if (!Array.isArray(comments) || !commentId) {
            return null;
        }

        for (const comment of comments) {
            if (!comment) {
                continue;
            }
            if (matchesComment(comment, commentId)) {
                return comment;
            }
            if (comment.replies && comment.replies.length > 0) {
                const nested = findCommentInTree(comment.replies, commentId);
                if (nested) {
                    return nested;
                }
            }
        }

        return null;
    };

    const removeTokensForTree = (commentNode) => {
        if (!commentNode) {
            return;
        }
        const nodeId = commentNode._id || commentNode.id;
        if (nodeId) {
            removeCommentToken(nodeId);
        }
        if (Array.isArray(commentNode.replies)) {
            commentNode.replies.forEach(child => removeTokensForTree(child));
        }
    };

    const handleAddComment = async (postId, note) => {
        try {
            const response = await axios.post(`${API_URL}/api/posts/${postId}/comments`, note);
            const savedComment = response.data;
            const commentId = savedComment._id || savedComment.id;

            if (savedComment.editToken) {
                saveCommentToken(commentId, savedComment.editToken, savedComment.editTokenExpiresAt);
            }

            const sanitizedComment = { ...savedComment };
            delete sanitizedComment.editToken;

            setSelectedPost(prev => {
                if (!prev || prev._id !== postId) {
                    return prev;
                }

                if (!note.parentId) {
                    const existingComments = Array.isArray(prev.comments) ? prev.comments : [];
                    return {
                        ...prev,
                        comments: [sanitizedComment, ...existingComments],
                    };
                }

                const updatedComments = insertReply(prev.comments || [], note.parentId, sanitizedComment);
                return {
                    ...prev,
                    comments: updatedComments,
                };
            });
        } catch (error) {
            console.error('[ERROR] Failed to log field note:', error);
            throw new Error(error.response?.data?.message || 'UNABLE TO LOG FIELD NOTE');
        }
    };

    const handleEditComment = async (postId, note) => {
        try {
            const response = await axios.patch(
                `${API_URL}/api/posts/${postId}/comments/${note.commentId}`,
                { content: note.content, editToken: note.editToken }
            );
            const updatedComment = response.data;
            const targetId = updatedComment._id || note.commentId;

            setSelectedPost(prev => {
                if (!prev || prev._id !== postId) {
                    return prev;
                }

                return {
                    ...prev,
                    comments: updateCommentContent(prev.comments || [], targetId, updatedComment),
                };
            });
        } catch (error) {
            console.error('[ERROR] Failed to update field note:', error);
            throw new Error(error.response?.data?.message || 'UNABLE TO UPDATE NOTE');
        }
    };

    const handleDeleteComment = async (postId, note) => {
        try {
            const targetComment = findCommentInTree(selectedPost?.comments || [], note.commentId);

            await axios.delete(
                `${API_URL}/api/posts/${postId}/comments/${note.commentId}`,
                { data: { editToken: note.editToken } }
            );

            removeTokensForTree(targetComment);

            setSelectedPost(prev => {
                if (!prev || prev._id !== postId) {
                    return prev;
                }

                return {
                    ...prev,
                    comments: removeComment(prev.comments || [], note.commentId),
                };
            });
        } catch (error) {
            console.error('[ERROR] Failed to delete field note:', error);
            throw new Error(error.response?.data?.message || 'UNABLE TO DELETE NOTE');
        }
    };

    const handleUpdatePost = async (postId, payload) => {
        try {
            const { adminToken: tokenFromPayload, ...body } = payload || {};
            const headers = tokenFromPayload ? { Authorization: `Bearer ${tokenFromPayload}` } : undefined;
            const response = await axios.patch(
                `${API_URL}/api/posts/${postId}`,
                body,
                headers ? { headers } : undefined
            );
            const updatedPost = response.data;

            setSelectedPost(prev => {
                if (!prev || prev._id !== postId) {
                    return prev;
                }

                return {
                    ...prev,
                    ...updatedPost,
                    comments: prev.comments,
                };
            });

            setPosts(prev =>
                prev.map(post => (post._id === postId ? { ...post, ...updatedPost } : post))
            );

            await fetchTags();
            await fetchAllTitles();

            return updatedPost;
        } catch (error) {
            console.error('[ERROR] Failed to update document:', error);
            if (error.response?.status === 401) {
                clearAdminSession();
            }
            throw new Error(error.response?.data?.message || 'UNABLE TO UPDATE DOCUMENT');
        }
    };

    const handleDeletePost = async (postId, payload) => {
        try {
            const tokenFromPayload = payload?.adminToken;
            const headers = tokenFromPayload ? { Authorization: `Bearer ${tokenFromPayload}` } : undefined;
            const data = tokenFromPayload ? {} : { passphrase: payload.passphrase };
            await axios.delete(
                `${API_URL}/api/posts/${postId}`,
                { data, ...(headers ? { headers } : {}) }
            );

            setPosts(prev => prev.filter(post => post._id !== postId));
            setSelectedPost(null);
            setSelectedPostId(null);
            await fetchPosts();
            await fetchTags();
            await fetchAllTitles();
        } catch (error) {
            console.error('[ERROR] Failed to delete document:', error);
            if (error.response?.status === 401) {
                clearAdminSession();
            }
            throw new Error(error.response?.data?.message || 'UNABLE TO DELETE DOCUMENT');
        }
    };

    const exitAuthorPortal = () => {
        setIsAuthorPortalOpen(false);
        setAuthorPassphrase('');
        setAuthorPosts([]);
        setAuthorPortalError(null);
        setAuthorLoading(false);
        setActiveAuthorTab('create');
        setAuthorAccessStatus(null);
        setIsMasterAccess(false);
        setMasterAccessCode('');
        clearAdminSession();
        handleReturnToList();
    };

    const handleAuthorPortalButton = async (initialTab = '') => {
        if (typeof initialTab !== 'string') {
            initialTab = '';
        }
        if (isAuthorPortalOpen) {
            exitAuthorPortal();
            fetchPosts();
            return;
        }

        navigate(OLD_BASE);

        if (adminToken) {
            setIsAuthorPortalOpen(true);
            if (initialTab) {
                setActiveAuthorTab(initialTab);
            }
            const writersResult = await fetchAdminWriters(adminToken);
            const desiredHash = adminSelectedAuthorHash || (writersResult.success ? writersResult.writers?.[0]?.authorHash : '');
            if (desiredHash) {
                setAdminSelectedAuthorHash(desiredHash);
                await fetchAdminAuthorPosts(desiredHash, adminToken, { suppressError: true });
                setActiveAuthorTab(initialTab || 'documents');
            }
            return;
        }

        setShowAuthorAccess(true);
        setAuthorAccessPassphrase('');
        setAuthorAccessError(null);
        setAuthorAccessStatus(null);
        setIsAuthorRegistration(false);
        setIsMasterAccess(false);
        setMasterAccessCode('');
        resetAuthorRegistrationForm();
        setAuthorRegistrationLoading(false);
    };

    const handleAuthorAccessClose = () => {
        setShowAuthorAccess(false);
        setAuthorAccessPassphrase('');
        setAuthorAccessError(null);
        setAuthorAccessStatus(null);
        setIsAuthorRegistration(false);
        setIsMasterAccess(false);
        setMasterAccessCode('');
        resetAuthorRegistrationForm();
        setAuthorRegistrationLoading(false);
    };

    const handleAuthorAccessSubmit = async (event) => {
        event.preventDefault();
        const trimmedPassphrase = authorAccessPassphrase.trim();

        if (!trimmedPassphrase) {
            setAuthorAccessError('Writer passphrase required');
            return;
        }

        setAuthorAccessError(null);
        setAuthorAccessStatus(null);

        clearAdminSession();
        const result = await fetchAuthorPosts(trimmedPassphrase, { suppressError: true });
        if (result.success) {
            setAuthorPassphrase(trimmedPassphrase);
            setIsAuthorPortalOpen(true);
            setShowAuthorAccess(false);
            setAuthorAccessPassphrase('');
            setAuthorPortalError(null);
            setIsAuthorRegistration(false);
            resetAuthorRegistrationForm();
            handleReturnToList();
        } else {
            setAuthorAccessError(result.message || 'Unable to verify writer credentials');
        }
    };

    const handleMasterAccessSubmit = async (event) => {
        event.preventDefault();
        const trimmedCode = masterAccessCode.trim();

        if (!trimmedCode) {
            setAuthorAccessError('Master code required');
            return;
        }

        setAuthorAccessError(null);
        setAuthorAccessStatus(null);
        setAuthorRegistrationError(null);

        try {
            const response = await axios.post(`${API_URL}/api/admin/unlock`, { code: trimmedCode });
            const token = response.data?.token || '';
            const expiresAt = response.data?.expiresAt || '';

            if (!token) {
                setAuthorAccessError('Failed to start admin session');
                return;
            }

            setAuthorPassphrase('');
            setAdminToken(token);
            setAdminTokenExpiresAt(expiresAt);
            sessionStorage.setItem('discerner_admin_token', token);
            if (expiresAt) {
                sessionStorage.setItem('discerner_admin_expires_at', expiresAt);
            }

            setShowAuthorAccess(false);
            setIsAuthorRegistration(false);
            setIsMasterAccess(false);
            setMasterAccessCode('');
            resetAuthorRegistrationForm();

            // Master unlock lands on the master dashboard, which links back
            // to the author portal (admin mode) and every admin page.
            navigate('/admin');
        } catch (error) {
            const message = error.response?.data?.message || 'Unable to unlock admin access';
            setAuthorAccessError(message);
        }
    };

    const handleAuthorAccessModeToggle = () => {
        setIsMasterAccess(false);
        setMasterAccessCode('');
        setIsAuthorRegistration((previous) => {
            const next = !previous;
            if (next) {
                setAuthorAccessPassphrase('');
            }
            return next;
        });
        setAuthorAccessError(null);
        setAuthorAccessStatus(null);
        setAuthorRegistrationError(null);
        setAuthorRegistrationPassphrase('');
        setAuthorRegistrationConfirm('');
        setAuthorInviteCode('');
        setAuthorRegistrationLoading(false);
    };

    const handleMasterAccessModeToggle = () => {
        setIsMasterAccess((previous) => {
            const next = !previous;
            if (next) {
                setIsAuthorRegistration(false);
                setAuthorAccessPassphrase('');
                resetAuthorRegistrationForm();
            }
            return next;
        });
        setMasterAccessCode('');
        setAuthorAccessError(null);
        setAuthorAccessStatus(null);
        setAuthorRegistrationError(null);
        setAuthorRegistrationLoading(false);
    };

    const handleAuthorRegistrationSubmit = async (event) => {
        event.preventDefault();

        const passphrase = authorRegistrationPassphrase.trim();
        const confirmPassphrase = authorRegistrationConfirm.trim();
        const inviteCode = authorInviteCode.trim();

        if (!passphrase) {
            setAuthorRegistrationError('Passphrase required');
            return;
        }

        if (passphrase.length < 8) {
            setAuthorRegistrationError('Passphrase must be at least 8 characters');
            return;
        }

        if (passphrase.length > 128) {
            setAuthorRegistrationError('Passphrase must be 128 characters or fewer');
            return;
        }

        if (passphrase !== confirmPassphrase) {
            setAuthorRegistrationError('Passphrases do not match');
            return;
        }

        if (!inviteCode) {
            setAuthorRegistrationError('Invite code required');
            return;
        }

        setAuthorRegistrationError(null);
        setAuthorAccessStatus(null);
        setAuthorRegistrationLoading(true);

        try {
            const response = await axios.post(`${API_URL}/api/writers/register`, {
                passphrase,
                inviteCode,
            });
            const message = response.data?.message || 'Registration successful';

            resetAuthorRegistrationForm();
            setAuthorAccessPassphrase(passphrase);
            setAuthorAccessStatus(message);
            setAuthorAccessError(null);
            setIsAuthorRegistration(false);
        } catch (submissionError) {
            const message = submissionError.response?.data?.message || 'Unable to register for writer access';
            setAuthorRegistrationError(message);
        } finally {
            setAuthorRegistrationLoading(false);
        }
    };

    const handleAuthorPostPublished = (newPost) => {
        if (newPost && newPost._id) {
            setAuthorPosts(prev => {
                const existing = Array.isArray(prev) ? prev.filter(post => post._id !== newPost._id) : [];
                return [newPost, ...existing];
            });
        }
        fetchPosts();
        fetchTags();
        fetchAllTitles();
    };

    const handleAuthorUpdatePost = async (postId, payload) => {
        const updatedPost = await handleUpdatePost(postId, payload);
        if (updatedPost) {
            setAuthorPosts(prev =>
                prev.map(post => (post._id === postId ? { ...post, ...updatedPost } : post))
            );
        }
        return updatedPost;
    };

    const handleAuthorDeletePost = async (postId, payload) => {
        await handleDeletePost(postId, payload);
        setAuthorPosts(prev => prev.filter(post => post._id !== postId));
        // The backend drops a deleted post from its series; refresh part numbers.
        if (authorSeriesByPost.has(String(postId))) {
            authorSeries.reload();
        }
    };

    const handleAdminAuthorChange = async (event) => {
        const nextHash = event.target.value;
        setAdminSelectedAuthorHash(nextHash);
        setActiveAuthorTab((currentTab) => (currentTab === 'series' ? 'series' : 'documents'));
        if (nextHash) {
            await fetchAdminAuthorPosts(nextHash, adminToken);
        } else {
            setAuthorPosts([]);
        }
    };

    const handleAdminRefresh = async () => {
        const writersResult = await fetchAdminWriters(adminToken);
        const desiredHash = adminSelectedAuthorHash || (writersResult.success ? writersResult.writers?.[0]?.authorHash : '');
        if (desiredHash) {
            setAdminSelectedAuthorHash(desiredHash);
            await fetchAdminAuthorPosts(desiredHash, adminToken);
        }
    };

    if (error) {
        return (
            <div className="error-state">
                [SYSTEM ERROR]: {error}
            </div>
        );
    }

    return (
        <ThemeProvider>
            <div className="app">
                <Header
                    isAuthorMode={isAuthorPortalOpen}
                    onAuthorPortalClick={handleAuthorPortalButton}
                    onNavigateHome={handleNavigateHome}
                    onNavigateAbout={handleNavigateAbout}
                    onNavigateNewSite={() => navigate('/')}
                    activePage={activePage}
                />
                <div
                    className={`container${isHomeRoute ? ' container--home' : ''}${isReaderRoute ? ' container--reader' : ''}`}
                    data-home-tab={isHomeRoute ? activeHomeTab : undefined}
                >
                    {isAuthorPortalOpen ? (
                        <main className="main-content author-portal">
                            <section className="author-posts-section">
                                <header className="author-posts-header">
                                    <div className="author-posts-title">
                                        <h2>{isAdminMode ? 'Admin Portal' : 'Author Portal'}</h2>
                                        <p>{isAdminMode ? 'Manage documents across all authors.' : 'Manage your documents in one place.'}</p>
                                        {isAdminMode && adminTokenExpiresAt && (
                                            <p className="author-access-status">Session expires: {adminTokenExpiresAt}</p>
                                        )}
                                    </div>
                                    <div className="author-posts-actions">
                                        {isAdminMode && (
                                            <div className="admin-author-controls">
                                                <label>
                                                    Author
                                                    <select
                                                        value={adminSelectedAuthorHash}
                                                        onChange={handleAdminAuthorChange}
                                                        disabled={adminWritersLoading}
                                                    >
                                                        <option value="">
                                                            {adminWritersLoading ? 'Loading…' : 'Select an author'}
                                                        </option>
                                                        {adminWriters.map((writer) => (
                                                            <option key={writer.authorHash} value={writer.authorHash}>
                                                                {writer.authorHash}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                                <button
                                                    type="button"
                                                    className="author-refresh-button"
                                                    onClick={handleAdminRefresh}
                                                    disabled={adminWritersLoading || authorLoading}
                                                >
                                                    Refresh
                                                </button>
                                                <button
                                                    type="button"
                                                    className="author-refresh-button"
                                                    onClick={() => navigate('/admin')}
                                                >
                                                    Master Dashboard
                                                </button>
                                                <button
                                                    type="button"
                                                    className="author-refresh-button"
                                                    onClick={exitAuthorPortal}
                                                >
                                                    End Admin Session
                                                </button>
                                            </div>
                                        )}
                                        <div className="author-tabs">
                                            <button
                                                type="button"
                                                className={`author-tab${activeAuthorTab === 'create' ? ' active' : ''}`}
                                                onClick={() => setActiveAuthorTab('create')}
                                            >
                                                Create Document
                                            </button>
                                            <button
                                                type="button"
                                                className={`author-tab${activeAuthorTab === 'documents' ? ' active' : ''}`}
                                                onClick={() => setActiveAuthorTab('documents')}
                                            >
                                                Previous Documents
                                            </button>
                                            <button
                                                type="button"
                                                className={`author-tab${activeAuthorTab === 'series' ? ' active' : ''}`}
                                                onClick={() => setActiveAuthorTab('series')}
                                            >
                                                My Series
                                            </button>
                                        </div>
                                    </div>
                                </header>
                                {activeAuthorTab === 'create' ? (
                                    <div className="author-create-panel">
                                        <WriterPublish
                                            onPublished={handleAuthorPostPublished}
                                            defaultPassphrase={authorPassphrase}
                                            adminToken={isAdminMode ? adminToken : ''}
                                            adminAuthorHash={isAdminMode ? adminSelectedAuthorHash : ''}
                                            defaultExpanded
                                            inline
                                            tagGroups={tagGroups}
                                        />
                                    </div>
                                ) : activeAuthorTab === 'series' ? (
                                    <div className="author-series-panel">
                                        <WriterSeries
                                            posts={authorPosts}
                                            postsLoading={authorLoading}
                                            passphrase={authorPassphrase}
                                            adminToken={isAdminMode ? adminToken : ''}
                                            adminAuthorHash={isAdminMode ? adminSelectedAuthorHash : ''}
                                            seriesState={authorSeries}
                                            onUnauthorized={clearAdminSession}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {authorPortalError && (
                                            <div className="error-state">
                                                [SYSTEM ERROR]: {authorPortalError}
                                            </div>
                                        )}
                                        {authorLoading ? (
                                            <div className="loading">[Retrieving Documents...]</div>
                                        ) : authorPosts.length === 0 ? (
                                            <div className="no-posts">
                                                {isAdminMode
                                                    ? 'Select an author to view documents.'
                                                    : 'No documents associated with this passphrase yet.'}
                                            </div>
                                        ) : (
                                            <div className="author-posts-list">
                                                {authorPosts.map(post => (
                                                    <article key={post._id} className="author-post-card">
                                                        <div className="author-post-summary">
                                                            <span className="copy-number">{formatDocumentNumber(post.documentNumber ?? post.copyNumber)}</span>
                                                            {isVideoPost(post) && (
                                                                <span className="author-post-kind" title="Listed on the Condoned Media — Videos page (/videos)">
                                                                    ▶ VIDEO
                                                                </span>
                                                            )}
                                                            <h3 className="author-post-title">{post.title}</h3>
                                                            <div className="post-meta">
                                                                <span>DATE: {post.date}</span>
                                                                <span>
                                                                    TAGS:{' '}
                                                                    {Array.isArray(post.tags)
                                                                        ? post.tags.join(', ')
                                                                        : post.tags}
                                                                </span>
                                                            </div>
                                                            <p className="author-post-description">{post.description}</p>
                                                            <AuthorPostSeriesLine membership={authorSeriesByPost.get(String(post._id))} />
                                                        </div>
                                                        <WriterControls
                                                            post={post}
                                                            onUpdate={(payload) => handleAuthorUpdatePost(post._id, payload)}
                                                            onDelete={(payload) => handleAuthorDeletePost(post._id, payload)}
                                                            defaultPassphrase={authorPassphrase}
                                                            adminToken={isAdminMode ? adminToken : ''}
                                                            tagGroups={tagGroups}
                                                        />
                                                    </article>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </section>
                        </main>
                    ) : (
                        <Routes>
                            <Route
                                path="about"
                                element={
                                    <main className="main-content full-width">
                                        <AboutPage onNavigateHome={handleNavigateHome} />
                                    </main>
                                }
                            />
                            <Route
                                path="posts/:postId"
                                element={
                                    <>
                                        <Sidebar
                                            tags={allTags}
                                            tagGroups={tagGroups}
                                            selectedTags={selectedTags}
                                            selectedTagSet={selectedTagSet}
                                            onTagClick={toggleTag}
                                            onSelectTags={selectTags}
                                            onClearFilters={clearFilters}
                                        />
                                        <main className="main-content">
                                            {!routePostId ? (
                                                <Navigate to={OLD_BASE} replace />
                                            ) : detailError ? (
                                                <section className="post-detail">
                                                    <div className="post-detail-panel">
                                                        <button type="button" className="back-button" onClick={handleReturnToList}>
                                                            Return to Home
                                                        </button>
                                                        <div className="error-state">
                                                            [SYSTEM ERROR]: {detailError}
                                                        </div>
                                                    </div>
                                                </section>
                                            ) : selectedPost ? (
                                                <PostDetail
                                                    post={selectedPost}
                                                    comments={selectedPost.comments || []}
                                                    onBack={handleReturnToList}
                                                    onAddComment={(payload) => handleAddComment(selectedPost._id, payload)}
                                                    onEditComment={(payload) => handleEditComment(selectedPost._id, payload)}
                                                    onDeleteComment={(payload) => handleDeleteComment(selectedPost._id, payload)}
                                                    onSelectPostId={handleSelectPostById}
                                                />
                                            ) : (
                                                <div className="loading">[Retrieving Document...]</div>
                                            )}
                                        </main>
                                    </>
                                }
                            />
                            <Route
                                path="/"
                                element={
                                    <>
                                        <div className="home-tabs" role="tablist" aria-label="Home columns">
                                            {HOME_TABS.map((tab) => {
                                                const isActive = tab.id === activeHomeTab;
                                                return (
                                                    <button
                                                        key={tab.id}
                                                        type="button"
                                                        className={`home-tab${isActive ? ' active' : ''}`}
                                                        role="tab"
                                                        aria-selected={isActive}
                                                        onClick={() => setActiveHomeTab(tab.id)}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <Sidebar
                                            tags={allTags}
                                            tagGroups={tagGroups}
                                            selectedTags={selectedTags}
                                            selectedTagSet={selectedTagSet}
                                            onTagClick={toggleTag}
                                            onSelectTags={selectTags}
                                            onClearFilters={clearFilters}
                                        />
                                        <main className="main-content">
                                            <PostList
                                                posts={posts}
                                                loading={loading}
                                                currentPage={postsPage}
                                                totalPages={postsTotalPages}
                                                onPageChange={handlePostsPageChange}
                                                selectedTags={selectedTags}
                                                selectedTagSet={selectedTagSet}
                                                onSelectPost={handleSelectPost}
                                                onActivePostChange={setActivePostId}
                                            />
                                        </main>
                                        <TitleIndex
                                            titles={allPostTitles}
                                            loading={titlesLoading}
                                            error={titlesError}
                                            activePostId={activePostId}
                                            onSelectPostId={handleSelectPostById}
                                        />
                                    </>
                                }
                            />
                            <Route path="*" element={<Navigate to={OLD_BASE} replace />} />
                        </Routes>
                    )}
                </div>

                {showAuthorAccess && (
                    <div className="author-access-overlay">
                        <div className="author-access-dialog">
                            <h2>
                                {isMasterAccess
                                    ? 'Master Access'
                                    : (isAuthorRegistration ? 'Request Writer Access' : 'Author Portal')}
                            </h2>
                            <p>
                                {isMasterAccess
                                    ? 'Enter the time-gated master code to start an admin session.'
                                    : (isAuthorRegistration
                                        ? 'Enter your invite code and choose a secure passphrase to activate writer access.'
                                        : 'Enter your assigned writer passphrase to access the portal.')}
                            </p>
                            {isMasterAccess ? (
                                <form onSubmit={handleMasterAccessSubmit} className="author-access-form">
                                    <label>
                                        Master Code
                                        <input
                                            type="password"
                                            value={masterAccessCode}
                                            onChange={(event) => setMasterAccessCode(event.target.value)}
                                            placeholder="6-digit code"
                                            autoFocus
                                            autoComplete="off"
                                        />
                                    </label>
                                    {authorAccessError && (
                                        <div className="author-access-error">[ERROR] {authorAccessError}</div>
                                    )}
                                    <div className="author-access-actions">
                                        <button
                                            type="button"
                                            className="author-access-cancel"
                                            onClick={handleAuthorAccessClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="author-access-submit"
                                        >
                                            Start Admin Session
                                        </button>
                                    </div>
                                </form>
                            ) : isAuthorRegistration ? (
                                <form onSubmit={handleAuthorRegistrationSubmit} className="author-access-form">
                                    <label>
                                        Choose Passphrase
                                        <input
                                            type="password"
                                            value={authorRegistrationPassphrase}
                                            onChange={(event) => setAuthorRegistrationPassphrase(event.target.value)}
                                            placeholder="Create a passphrase"
                                            autoFocus
                                        />
                                    </label>
                                    <label>
                                        Confirm Passphrase
                                        <input
                                            type="password"
                                            value={authorRegistrationConfirm}
                                            onChange={(event) => setAuthorRegistrationConfirm(event.target.value)}
                                            placeholder="Re-enter passphrase"
                                        />
                                    </label>
                                    <label>
                                        Invite Code
                                        <input
                                            type="text"
                                            value={authorInviteCode}
                                            onChange={(event) => setAuthorInviteCode(event.target.value)}
                                            placeholder="Enter invite code"
                                        />
                                    </label>
                                    {authorRegistrationError && (
                                        <div className="author-access-error">[ERROR] {authorRegistrationError}</div>
                                    )}
                                    <div className="author-access-actions">
                                        <button
                                            type="button"
                                            className="author-access-cancel"
                                            onClick={handleAuthorAccessClose}
                                            disabled={authorRegistrationLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="author-access-submit"
                                            disabled={authorRegistrationLoading}
                                        >
                                            {authorRegistrationLoading ? 'Registering…' : 'Register Access'}
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleAuthorAccessSubmit} className="author-access-form">
                                    <label>
                                        Writer Passphrase
                                        <input
                                            type="password"
                                            value={authorAccessPassphrase}
                                            onChange={(event) => setAuthorAccessPassphrase(event.target.value)}
                                            placeholder="Enter passphrase"
                                            autoFocus
                                        />
                                    </label>
                                    {authorAccessStatus && (
                                        <div className="author-access-status">{authorAccessStatus}</div>
                                    )}
                                    {authorAccessError && (
                                        <div className="author-access-error">[ERROR] {authorAccessError}</div>
                                    )}
                                    <div className="author-access-actions">
                                        <button
                                            type="button"
                                            className="author-access-cancel"
                                            onClick={handleAuthorAccessClose}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="author-access-submit"
                                            disabled={authorLoading}
                                        >
                                            {authorLoading ? 'Verifying…' : 'Enter Portal'}
                                        </button>
                                    </div>
                                </form>
                            )}
                            <button
                                type="button"
                                className="author-access-toggle"
                                onClick={handleAuthorAccessModeToggle}
                                disabled={authorRegistrationLoading || isMasterAccess}
                            >
                                {isAuthorRegistration
                                    ? `Already have a passphrase? Access the portal. Want to get an invite code? Email the admin at "Admin@discerner.blog"`
                                    : 'Have an invite code? Register for writer access'}
                            </button>
                            <button
                                type="button"
                                className="author-access-toggle"
                                onClick={handleMasterAccessModeToggle}
                                disabled={authorRegistrationLoading}
                            >
                                {isMasterAccess ? 'Back to writer login' : 'Use master code (admin)'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </ThemeProvider>
    );
}

export default App;
